import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ElaborationDocumentFilter,
  ElaborationDocumentFilterEntity, ElaborationDocumentFilterSezione,
  ElaborationDocumentFilterTopic,
  ElaborationDocumentFilterWrapper,
  ElaborationDocumentOccorrenzaMetadata,
  ElaborationDocumentOccorrenzaMetadataType,
  TIPOLOGIA_FILTER
} from '../models/elaboration-doc-filter';
import { ElaborationDocument } from '../models/elaboration-doc';
import { ELABORATION_DOC_STATUS } from "../models/elaboration-doc-status";
import { TipoTipologica } from 'src/app/common/models/tipologiche/tipo-tipologica';


@Injectable({
  providedIn: 'root'
})
export class DocumentManagementStoreService {

  private readonly CHARPAGE: number = 1000;

  private elaborationBS$: BehaviorSubject<ElaborationDocument> = new BehaviorSubject<ElaborationDocument>(null);
  private topicsBS$: BehaviorSubject<ElaborationDocumentFilter[]> = new BehaviorSubject<ElaborationDocumentFilter[]>([]);
  private sectionsBS$: BehaviorSubject<ElaborationDocumentFilter[]> = new BehaviorSubject<ElaborationDocumentFilter[]>([]);
  private entitiesBS$: BehaviorSubject<ElaborationDocumentFilter[]> = new BehaviorSubject<ElaborationDocumentFilter[]>([]);
  private currentPageTextBS$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private pageTextChuncksBS$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  private currentPagesNumberBS$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private pagesNumberBS$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private filtersSelectedBS$: BehaviorSubject<ElaborationDocumentFilter[]> = new BehaviorSubject<ElaborationDocumentFilter[]>([]);

  elaboration$: Observable<ElaborationDocument> = this.elaborationBS$.asObservable();
  topics$: Observable<ElaborationDocumentFilter[]> = this.topicsBS$.asObservable();
  sections$: Observable<ElaborationDocumentFilter[]> = this.sectionsBS$.asObservable();
  entities$: Observable<ElaborationDocumentFilter[]> = this.entitiesBS$.asObservable();
  currentPage$: Observable<string> = this.currentPageTextBS$.asObservable();
  pagesNumber$: Observable<number> = this.pagesNumberBS$.asObservable()

  constructor() {
  }

  setElaboration(elaboration: ElaborationDocument) {
    if (elaboration.stato.label.toLowerCase() === ELABORATION_DOC_STATUS.ELABORATO) {
      this.pageTextChuncksBS$.next(this.chunkSubstr(elaboration.documentiElaborati[0].testoEstratto, this.CHARPAGE));
      this.pagesNumberBS$.next(this.pageTextChuncksBS$.value.length);
      this.currentPagesNumberBS$.next(0);
    }
    this.filtersSelectedBS$.next([]);
    this.elaborationBS$.next(elaboration);
  }

  updateTopics(topics: ElaborationDocumentFilterTopic[]) {
    // costruisco wrapper per i topic: un array dove, per ogni topic, è presente la lista delle sue occorrenze
    let topicsWrapper: ElaborationDocumentFilterWrapper[] = this.getFilterWrapper(topics);
    let filters: ElaborationDocumentFilter[] = topicsWrapper.map(t => {
      return {
        tipo: TIPOLOGIA_FILTER.TOPIC,
        wrapper: t
      }
    });
    this.topicsBS$.next(filters);
  }

  updateEntities(entities: ElaborationDocumentFilterEntity[]) {
    // costruisco wrapper per le entita: un array dove, per ogni entita, è presente la lista delle sue occorrenze
    let EntitiesWrapper: ElaborationDocumentFilterWrapper[] = this.getFilterWrapper(entities);
    let filters: ElaborationDocumentFilter[] = EntitiesWrapper.map(e => {
      return {
        tipo: TIPOLOGIA_FILTER.ENTITY,
        wrapper: e
      }
    });
    this.entitiesBS$.next(filters);
  }

  updateSections(sections: ElaborationDocumentFilterSezione[]) {
    // costruisco wrapper per le sezione: un array dove, per ogni sezione, è presente la lista delle sue occorrenze
    let EntitiesWrapper: ElaborationDocumentFilterWrapper[] = this.getFilterWrapper(sections);
    let filters: ElaborationDocumentFilter[] = EntitiesWrapper.map(s => {
      return {
        tipo: TIPOLOGIA_FILTER.SEZIONE,
        wrapper: s
      }
    });
    this.sectionsBS$.next(filters);
  }

  aggiungiFiltro(filtro: ElaborationDocumentFilter) {

    if (filtro && filtro.wrapper && filtro.wrapper.occorrenze && filtro.wrapper.occorrenze.length > 0) {
      this.filtersSelectedBS$.next([...this.filtersSelectedBS$.value, filtro]);
      this.onPageChanges();
    }
  }

  rimuoviFiltro(filtro: ElaborationDocumentFilter) {
    this.filtersSelectedBS$.next(this.filtersSelectedBS$.value.filter(el => el.wrapper.tipo.id !== filtro.wrapper.tipo.id));
    this.onPageChanges();
  }

  changePage(page: number) {
    this.currentPagesNumberBS$.next(page);
    this.onPageChanges();
  }

  // FIXME: sostituire any[] con tipo specializzato
  // private getFilterWrapper(metadata: ElaborationDocumentFilterTopic[] | ElaborationDocumentFilterSezione[] | ElaborationDocumentFilterEntity[]) {
  private getFilterWrapper(metadata: any[]) {
    let metadataWrappers: ElaborationDocumentFilterWrapper[] = [];
    if (metadata) {
      // itero tutti i metadati ricevuti. Se un metadato (tipologia) è gia presente allora aggiorna la lista delle sue occorrenze, altrimenti aggiorno array wrapper
      metadata.forEach(m => {
        let tipoMetadato: TipoTipologica = m.tipoTopic ? m.tipoTopic : m.tipoEntita ? m.tipoEntita : m.tipoSezione;
        let metadataWrapperObj = metadataWrappers.find(mw => tipoMetadato.id == mw.tipo.id);
        if (!metadataWrapperObj) {
          metadataWrapperObj = {
            tipo: tipoMetadato,
            occorrenze: []
            // ,
            // valore: m.valore
          };
          metadataWrappers.push(metadataWrapperObj);
        }
        if (m.inizio != undefined && m.fine != undefined) {
          // se il metadato è presente una sola volta, prendo la singola occorrenza
          let occorrenzaMetadata: ElaborationDocumentOccorrenzaMetadata = {
            inizio: m.inizio,
            fine: m.fine
          };
          metadataWrapperObj.occorrenze.push(occorrenzaMetadata);
        } else if (m.occorrenze) {
          // altrimenti, se il metadato è presente più volte, prendo tutte le sue occorrenze
          m.occorrenze.forEach(occorrenza => {
            metadataWrapperObj.occorrenze.push({
              inizio: occorrenza.inizio,
              fine: occorrenza.fine
            });
          });
        }
      });
    }
    // rimuovo metadati che non hanno occorrenze
    metadataWrappers = metadataWrappers.filter(mw => mw.occorrenze && mw.occorrenze.length > 0);
    return metadataWrappers;
  }

  private onPageChanges() {
    let pageText: string = this.getPageText();
    this.currentPageTextBS$.next(pageText);
  }

  private getPageText(): string {
    const currentPage = this.currentPagesNumberBS$.value;
    let pageText: string = this.pageTextChuncksBS$.value[currentPage];

    let filtersSelected: ElaborationDocumentFilter[] = this.filtersSelectedBS$.value;
    if (filtersSelected && filtersSelected.length > 0) {
      // ottengo tutte le occorrenze per i filtri selezionati
      let occorrenzeFilter: ElaborationDocumentOccorrenzaMetadataType[] = [];
      filtersSelected.forEach((f: ElaborationDocumentFilter) => {
        f.wrapper.occorrenze.forEach(o => {
          occorrenzeFilter = occorrenzeFilter.concat([
            {
              tipo: f.tipo,
              isApertura: true,
              indice: o.inizio,
              pageStart : o.inizio > 0 ?  Math.ceil(o.inizio / this.CHARPAGE) : 1,
              pageEnd : Math.ceil(o.fine / this.CHARPAGE)

            },
            {
              tipo: f.tipo,
              isApertura: false,
              indice: o.fine,
              pageStart : o.inizio > 0 ?  Math.ceil(o.inizio / this.CHARPAGE) : 1,
              pageEnd : Math.ceil(o.fine / this.CHARPAGE)
            },
          ])
        })
      })

      // calcolo indici di inizio e fine pagina corrente
      const startPageIndex: number = (this.currentPagesNumberBS$.value * this.CHARPAGE);
      const endPageIndex: number = (((this.currentPagesNumberBS$.value + 1) * this.CHARPAGE) - 1);
      // contantore di caratteri aggiuntivi per le occorrenze evidenziate
      let numeroCaratteriAggiuntivi: number = 0;

      // itero occorrenze per tag di apertura/chiusura ordinando per indice
      occorrenzeFilter.sort((o1, o2) => o1.indice - o2.indice);
      occorrenzeFilter.forEach(occorrenza => {
        // costruisco tag span per l'occorrenza in base al suo tipo
        const openingTag: string = `<span class="${occorrenza.tipo}">`;
        // costruisco chiusura tag span per l'occorrenza
        const closingTag: string = '</span>';


        if(occorrenza.pageStart - 1  < currentPage && occorrenza.pageEnd > currentPage +1){
          let occorrenzaInizio
          if (occorrenza.isApertura) {
            occorrenzaInizio = 0;
            pageText = pageText.substring(0, occorrenzaInizio) + openingTag + pageText.substring(occorrenzaInizio, pageText.length);
            numeroCaratteriAggiuntivi += openingTag.length;
          } else {
            occorrenzaInizio = pageText.length + (numeroCaratteriAggiuntivi);
            pageText = pageText.substring(0, occorrenzaInizio) + closingTag + pageText.substring(occorrenzaInizio, pageText.length);
            numeroCaratteriAggiuntivi += closingTag.length;
          }

        }else if(occorrenza.pageStart - 1  < currentPage && occorrenza.pageEnd === currentPage +1){
          let occorrenzaInizio
          if (occorrenza.isApertura) {
            occorrenzaInizio = 0;
            pageText = pageText.substring(0, occorrenzaInizio) + openingTag + pageText.substring(occorrenzaInizio, pageText.length);
            numeroCaratteriAggiuntivi += openingTag.length;
          } else {
            occorrenzaInizio = occorrenza.indice < startPageIndex ? 0 : (occorrenza.indice % this.CHARPAGE) + (numeroCaratteriAggiuntivi);
            pageText = pageText.substring(0, occorrenzaInizio) + closingTag + pageText.substring(occorrenzaInizio, pageText.length);
            numeroCaratteriAggiuntivi += closingTag.length;
          }

        } else if ((occorrenza.indice >= startPageIndex && occorrenza.indice < endPageIndex) || (occorrenza.indice < startPageIndex && occorrenza.indice > startPageIndex)) {
          // costruisco indice di inizio occorrenza in base alla pagina corrente e alle eventuali altre occorrenze gia evidenziate
          let occorrenzaInizio: number = occorrenza.indice < startPageIndex ? 0 : (occorrenza.indice % this.CHARPAGE) + (numeroCaratteriAggiuntivi);
          if (occorrenza.isApertura) {
            pageText = pageText.substring(0, occorrenzaInizio) + openingTag + pageText.substring(occorrenzaInizio, pageText.length);
            numeroCaratteriAggiuntivi += openingTag.length;
          } else {
            pageText = pageText.substring(0, occorrenzaInizio) + closingTag + pageText.substring(occorrenzaInizio, pageText.length);
            numeroCaratteriAggiuntivi += closingTag.length;
          }

        }
      });
    }

    return pageText;
  }

  private chunkSubstr(str, size) {
    let chunks = [];
    if (str) {
      try {
        const decodedString: string = decodeURIComponent(escape(window.atob(str)));
        const numChunks = Math.ceil(decodedString.length / size);

        for (let i = 0, o = 0; i < numChunks; ++i, o += size) {
          chunks[i] = decodedString.substr(o, size)
        }
      } catch (e) {

      }
    }

    return chunks;
  }

}
